import React, { useState, useEffect } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileStack,
  GripVertical,
  Loader2,
  RotateCcw,
  Send,
  Sparkles,
} from "lucide-react";
import Headerdash from "../head";
import Sidebar from "../sidebar";
import SpacingBox from "./Spacing";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useParams } from "react-router-dom";
import useFetchData from "../fetchpostData";
import ScoreBoxForReOrderPara from "./ResponseBoxForReOrderPara";
import Notification from "./Notification";
import ScoreCardForReading from "./ScoreBoxForReadingSection";
import Timers from "../PageComponent/Timers";

import Sentence from "./DragAndDrop"; // Update the import to the correct component path

const ReadingShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const StatusPill = ({ type, children }) => {
  const styles =
    type === "success"
      ? "bg-green-50 text-green-700 ring-green-100"
      : type === "warning"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : "bg-purple-50 text-purple-700 ring-purple-100";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${styles}`}
    >
      {children}
    </span>
  );
};

export default function ReOrderParagraphPage() {
  const { title } = useParams();
  const section = "reorderparagraph";
  const practiceItemId = title;

  const post = useFetchData(section, title); // Use the hook

  const [originalSentences, setOriginalSentences] = useState([]);
  const [draggableSentences, setDraggableSentences] = useState([]);
  const [Title, setTitle] = useState("");
  const [originalAnswer, setOriginalAnswer] = useState("");
  const [userId, setUserId] = useState("");
  const [username, setUsername] = useState("");
  const [practiceType, setPracticeType] = useState("");
  const [isAttempted, setIsAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [savedPractice, setSavedPractice] = useState(null); // New state to store saved practice data
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (post && post.q_title) {
      setTitle(post.q_title);
      const fetchedSentences = post.option.map((option) => ({
        ...option,
        text: option.options,
        originalIndex: option.index - 1, // Adjusting index to be 0-based
      }));

      setOriginalSentences(fetchedSentences);
      const shuffledDraggable = shuffleArray([...fetchedSentences]);
      setDraggableSentences(shuffledDraggable);
    }
  }, [post]);

  const moveSentence = (dragIndex, hoverIndex) => {
    const newSentences = [...draggableSentences];
    const draggedSentence = newSentences[dragIndex];

    newSentences.splice(dragIndex, 1);
    newSentences.splice(hoverIndex, 0, draggedSentence);
    setDraggableSentences(newSentences);
  };

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const savePractice = async (practiceData) => {
    try {
      const response = await fetch("/api/save-reorderparagraph", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(practiceData),
      });

      if (!response.ok) {
        throw new Error("Failed to save practice data");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitted(true);
    const userOrder = draggableSentences.map(
      (sentence) => sentence.originalIndex + 1
    );
    console.log("User's order of sentences:", userOrder.join(","));
    const answer = userOrder.join(",");
    const practiceType = section;
    setOriginalAnswer(answer);

    const practiceData = {
      practiceType,
      practiceItemId,
      response: {
        questionId: practiceItemId,
        answer,
        isAttempted,
      },
    };
    console.log("practiceData:", practiceData);
    try {
      const result = await savePractice(practiceData);
      console.log("Practice data saved:", result);
      setMessage("Practice data saved successfully!");
      setNotificationType("success");
      setSavedPractice(result);
      setShowNotification(true); // Show notification

      setTimeout(() => {
        setIsAttempted(true);
        setLoading(false);
        setSubmitted(true);
      }, 2000);
    } catch (error) {
      console.error("Error saving practice data:", error);
      setMessage("Failed to save practice data.");
      setNotificationType("error");
      setShowNotification(true); // Show notification
      setLoading(false);
      setSubmitted(false);
    }
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  console.log("savedPractice", savedPractice);

  // Callback function to handle the isAttempted value from the child component
  const handleIsAttempted = (isAttemptedValue) => {
    setIsAttempted(isAttemptedValue);
  };

  const handleRetake = () => {
    setSubmitted(false);
    setIsAttempted(false);
    const shuffledDraggable = shuffleArray([...originalSentences]);
    setDraggableSentences(shuffledDraggable);
    setSubmitted(false);
  };
  const attemptedStatus = isAttempted ? "Attempted" : "Not Attempted";

  return (
    <ReadingShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Reading practice
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {Title || "Re-order paragraphs"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Arrange the text boxes into the most logical paragraph order.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill>Medium</StatusPill>
          <StatusPill type={isAttempted ? "success" : "warning"}>
            {attemptedStatus}
          </StatusPill>
          <span className="inline-flex min-h-9 items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200">
            <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
            <Timers initialSeconds={0} />
          </span>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
        <div className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <FileStack className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Instructions
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Restore the paragraph order
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  The text boxes have been placed in a random order. Drag the
                  sentences into the sequence that creates the strongest flow.
                </p>
              </div>
            </div>
          </section>

          <DndProvider backend={HTML5Backend}>
            <section className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-blue-50 text-blue-700">
                    <FileStack className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase text-gray-500">
                      Reference
                    </p>
                    <h2 className="text-lg font-extrabold text-gray-900">
                      Original sentences
                    </h2>
                  </div>
                </div>

                <div className="space-y-3 rounded-md bg-slate-50 p-3 ring-1 ring-slate-100">
                  {originalSentences.map((sentence, index) => (
                    <div
                      key={sentence.id}
                      className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-4 text-sm font-semibold leading-6 text-gray-700 shadow-sm"
                    >
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-blue-50 text-xs font-extrabold text-blue-700 ring-1 ring-blue-100">
                        {index + 1}
                      </span>
                      <span>{sentence.options}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                    <GripVertical className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase text-gray-500">
                      Workspace
                    </p>
                    <h2 className="text-lg font-extrabold text-gray-900">
                      Draggable order
                    </h2>
                  </div>
                </div>

                <div className="space-y-3 rounded-md bg-slate-50 p-3 ring-1 ring-slate-100">
                  {draggableSentences.map((sentence, index) => (
                    <Sentence
                      key={sentence.id}
                      sentence={sentence}
                      index={index}
                      correctIndex={sentence.originalIndex}
                      moveSentence={moveSentence}
                      submitted={submitted}
                    />
                  ))}
                </div>
              </div>
            </section>
          </DndProvider>

          <section className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Ready to score?
                </p>
                <p className="text-sm text-gray-500">
                  Submit when the paragraph flow looks complete.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                onClick={handleSubmit}
                disabled={submitted}
              >
                {loading ? (
                  <>
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Submit
                  </>
                )}
              </button>

              {submitted && (
                <button
                  type="button"
                  onClick={handleRetake}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-slate-100 px-5 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Retake
                </button>
              )}
            </div>
          </section>

          {submitted ? (
            <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-green-50 text-green-700">
                  <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    Result
                  </p>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Latest score
                  </h2>
                </div>
              </div>
              <div className="flex justify-center py-6">
                <ScoreCardForReading
                  score={savedPractice?.savedResponse.totalScore}
                  maxScore={3}
                />
              </div>
            </section>
          ) : null}
        </div>

        <aside className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Practice tip
                </p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Look for connectors, pronoun references, and cause-effect
                  signals. The strongest order usually reads naturally from
                  context to detail.
                </p>
              </div>
            </div>
          </section>

          <ScoreBoxForReOrderPara
            // originalWords={originalAnswer}
            practiceItemId={practiceItemId}
            onIsAttempted={handleIsAttempted}
          />
        </aside>
      </div>

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={handleCloseNotification}
        />
      )}
      <SpacingBox />
    </ReadingShell>
  );
}
